import { useEffect, useState, type RefObject } from 'react';
import { useCompactSidebarLayout } from './useCompactSidebarLayout';

/** Keeps sidebars below the sticky header on desktop home. */
export const SIDEBAR_STICKY_TOP_MAX = 72;

/**
 * Sticky `top` so a sidebar scrolls with the page in sync with the feed, then
 * pins when its own bottom reaches the viewport bottom (not the taller sibling's).
 */
export function computeSidebarStickyTop(
  sidebarHeight: number,
  viewportHeight: number = typeof window !== 'undefined' ? window.innerHeight : 0,
): number {
  const computedTop = viewportHeight - sidebarHeight;
  return Math.min(SIDEBAR_STICKY_TOP_MAX, computedTop);
}

export function useSidebarStickyTop(elementRef: RefObject<HTMLElement | null>): number {
  const isCompactSidebar = useCompactSidebarLayout();
  const [stickyTop, setStickyTop] = useState(0);

  useEffect(() => {
    if (isCompactSidebar) {
      setStickyTop(0);
      return;
    }

    const el = elementRef.current;
    if (!el) return;

    const update = () => {
      const height = el.getBoundingClientRect().height;
      setStickyTop(computeSidebarStickyTop(height));
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isCompactSidebar, elementRef]);

  return stickyTop;
}
